<template>
    <div class="card p-shadow-app">
        <Toast />
        <Loading :active.sync="loading" :isFullPage="false"></Loading>

        <div v-if="form.tipodoc" class="p-mb-4">
            <h4>
                {{ isEditMode ? `#${dossierNumber} - ${title}` : `#${nextDossierNumber} - ${title}` }}
            </h4>
            <small class="p-d-block p-mb-3">Os campos marcados com * são obrigatórios.</small>
        </div>

        <div class="p-mb-4" v-if="form.tipodoc === 11 && transport_document_extra_info">
            <b>Guia de transporte (AT):</b>
            {{ transport_document_extra_info.atcodeid || 'Sem informação adicional' }}
        </div>

        <div v-if="form.tipodoc && canSeeDossierIfExists && !loading"
             class="p-mb-4 p-p-3 p-border-1 p-border-dashed p-border-secondary">
            Nenhum dossier encontrado.
        </div>

        <form @submit.prevent="submitForm" v-else>
            <div class="p-fluid p-formgrid p-grid p-d-flex p-jc-between">
                <div class="p-field p-col-12 p-md-4">
                    <label for="tipodoc">Tipo *</label>
                    <Dropdown
                        :disabled="isEditMode"
                        id="tipodoc"
                        v-model="form.tipodoc"
                        :options="dossierTypes"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Selecione o tipo"
                        :class="{ 'p-invalid': formErrors.tipodoc }"
                        @change="handleTypeChange"
                        :loading="loadingTypes"
                    />
                    <small v-if="formErrors.tipodoc" class="p-error">{{ formErrors.tipodoc }}</small>
                </div>
                <div v-if="form.tipodoc && formConfig" class="p-field p-col-12 p-md-2">
                    <span class="p-mt-2 p-d-flex">
                        <label for="closedSwitch" class="p-mr-2"><strong>Fechado:</strong></label>
                        <InputSwitch id="closedSwitch" v-model="form.closed" v-if="canEdit" />
                        <span v-else>{{ form.closed ? 'Sim' : 'Não' }}</span>
                    </span>
                </div>
            </div>

            <div v-if="form.tipodoc && formConfig" class="p-fluid p-formgrid p-grid">
                <AppDynamicField
                    v-for="(fieldConfig, fieldKey) in headerFields"
                    :key="fieldKey"
                    :fieldKey="fieldKey"
                    :fieldConfig="fieldConfig"
                    v-model="form[fieldKey]"
                    :error="formErrors[fieldKey]"
                    :canEdit="canEdit"
                    :form="form"
                    @update-form-data="mergeFormData"
                />
            </div>

            <template v-if="form.tipodoc && formConfig && formConfig.form_fields.lines">
                <AppDynamicTableLines
                    :key="`lines-${form.tipodoc}`"
                    :linesConfig="formConfig.form_fields.lines"
                    v-model="form.lines"
                    :useVat="formConfig.settings.calculate_vat"
                    :defaultWarehouse="formConfig.settings.default_warehouse || 1"
                    :canEdit="canEdit"
                />

                <div class="p-grid p-mt-4">
                    <div class="p-col-12 p-md-3 p-md-offset-9">
                        <div class="p-d-flex p-jc-between p-mb-2">
                            <strong>Total Ilíquido:</strong>
                            <span>{{ formatCurrency(totals.totalIliquido) }}</span>
                        </div>
                        <div v-if="totals.discount" class="p-d-flex p-jc-between p-mb-2">
                            <strong>Desconto:</strong>
                            <span>{{ formatCurrency(totals.discount) }}</span>
                        </div>
                        <div v-if="formConfig.settings.calculate_vat" class="p-d-flex p-jc-between p-mb-2">
                            <strong>IVA:</strong>
                            <span>{{ formatCurrency(totals.totalIva) }}</span>
                        </div>
                        <div class="p-d-flex p-jc-between p-mb-2">
                            <strong class="p-text-lg">Total:</strong>
                            <span class="p-text-lg p-text-bold">{{ formatCurrency(totals.total) }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <div v-if="form.tipodoc" class="p-mt-4">
                <Button v-if="canEdit" type="submit" :label="isEditMode ? 'Atualizar Dossier' : 'Guardar Dossier'"
                        icon="pi pi-check" class="p-mr-2" :loading="loading" />
                <Button type="button" label="Voltar" icon="pi pi-times" class="p-button-secondary" @click="cancelForm" />
            </div>
        </form>
    </div>
</template>

<script>
import dossierService from '@/services/internalDossier.service';
import AppDynamicField from '@/components/internalDossier/AppDynamicField.vue';
import AppDynamicTableLines from '@/components/internalDossier/AppDynamicTableLines.vue';
import Loading from "vue-loading-overlay";

export default {
    name: "InternalDossierForm",
    storageKey: "internal_dossier_conversion_draft",
    components: { AppDynamicField, AppDynamicTableLines, Loading },
    props: {
        isPatMode: { type: Boolean, default: false },
        patId: { type: Number, default: null },
        dossierReference: { type: String, default: null },
        dossierType: { type: [String, Number], default: null },
        initialDraft: { type: Object, default: null }
    },
    data() {
        return {
            title: 'Dossier Interno',
            isEditMode: false,
            form: { closed: false, tipodoc: null, lines: [] },
            dossierTypes: [],
            formConfig: null,
            loading: false,
            loadingTypes: false,
            formErrors: {},
            canEdit: false,
            nextDossierNumber: '',
            transport_document_extra_info: null,
            dossierStamp: null,
            dossierNumber: null
        };
    },
    computed: {
        canSeeDossierIfExists() { return !this.canEdit && this.dossierStamp == null; },
        headerFields() {
            if (!this.formConfig?.form_fields) return {};
            return Object.fromEntries(Object.entries(this.formConfig.form_fields).filter(([key]) => key !== 'lines'));
        },
        totals() {
            if (!this.form.lines.length || !(this.form.tipodoc && this.formConfig)) {
                return { totalIliquido: 0, discount: 0, totalIva: 0, total: 0 };
            }
            const totalIliquido = this.form.lines.reduce((s, l) => s + (l.sale_price || 0) * (l.quantity || 0), 0);
            const totalIva = this.formConfig.settings.calculate_vat ? this.form.lines.reduce((s, l) => s + (l.vat_value || 0), 0) : 0;
            const discount = this.form.lines.reduce((s, l) => s + (l.discount_value || 0), 0);
            return { totalIliquido, discount, totalIva, total: (totalIliquido - discount) + totalIva };
        }
    },
    async created() {
        await this.loadDossierTypes();

        if (this.initialDraft) {
            await this.loadProvidedDraft(this.initialDraft);
        } else if (!this.isPatMode && !this.dossierReference && this.$route.query.draft === '1') {
            await this.loadConversionDraft();
        } else if (this.isPatMode) {
            this.form.tipodoc = parseInt(this.dossierType) || null;
            this.dossierStamp = this.dossierReference;
            await this.loadDossier();
        } else if (this.dossierReference) {
            this.dossierStamp = this.dossierReference;
            this.isEditMode = true;
            await this.loadDossier();
        }
    },
    methods: {
        async loadProvidedDraft(response) {
            try {
                this.loading = true;
                const dossierData = response.form;

                this.canEdit = response.can_edit;
                this.form.tipodoc = parseInt(dossierData.type_key) || null;
                this.formConfig = dossierData.schema;
                this.title = dossierData.title || this.title;
                this.nextDossierNumber = response.next_obra_no || '';
                this.isEditMode = false;
                this.dossierStamp = null;

                this.initTransportFields();
                this.initializeFormFields();
                this.mapValues(dossierData.values || {});
            } catch (error) {
                this.showError('Erro ao carregar rascunho de conversão');
            } finally {
                this.loading = false;
            }
        },
        async loadConversionDraft() {
            const rawDraft = sessionStorage.getItem(this.$options.storageKey);
            if (!rawDraft) {
                this.$router.replace({ name: 'InternalDossiersFormView' }).catch(() => {});
                return;
            }

            try {
                await this.loadProvidedDraft(JSON.parse(rawDraft));
            } catch (error) {
                this.showError('Erro ao carregar rascunho de conversão');
            } finally {
                sessionStorage.removeItem(this.$options.storageKey);
                this.$router.replace({ name: 'InternalDossiersFormView' }).catch(() => {});
            }
        },
        async handleTypeChange() {
            if (this.isPatMode) await this.loadDossier();
            else await this.onDossierTypeChange();
        },

        async loadDossier() {
            if (!this.dossierStamp && !this.form.tipodoc) return;
            try {
                this.loading = true;
                let response;

                if (this.isPatMode) {
                    response = await dossierService.getPatFormInternalDossier(this.patId, {
                        type: this.form.tipodoc,
                        reference: this.dossierStamp
                    });
                } else {
                    response = await dossierService.getDossierDataFormConfig(
                        this.dossierStamp,
                    );
                }

                const dossierData = response.form;
                this.canEdit = response.can_edit;
                this.formConfig = dossierData.schema || dossierData; // Handle schema vs full object
                this.title = dossierData.title || this.title;
                this.form.tipodoc = parseInt(dossierData.type_key) || null;
                this.dossierNumber = dossierData.dossier_number;

                if (Object.keys(dossierData).includes('dossier_reference')) {
                    this.isEditMode = true;
                    this.dossierStamp = dossierData.dossier_reference || this.dossierStamp;
                    this.mapValues(dossierData.values || {});
                } else {
                    this.initTransportFields();
                    this.initializeFormFields();
                    this.nextDossierNumber = response.next_obra_no || '';
                    this.form.lines = [];
                    if (dossierData.values?.pat_id) this.$set(this.form, 'pat_id', dossierData.values.pat_id);
                    if (dossierData.values?.client_id) {
                        const estab = dossierData.values.client_estab !== undefined ? dossierData.values.client_estab : 0;
                        this.$set(this.form, 'client_id', `${dossierData.values.client_id}-${estab}`);
                        this.$set(this.form, 'client_estab', estab);
                    }
                }
            } catch (e) {
                this.showError('Erro ao carregar dados do dossier');
            } finally { this.loading = false; }
        },

        mapValues(values) {
            this.transport_document_extra_info = values.transport_document_extra_info || null;
            this.form.closed = values.closed || false;
            if (values.closed) this.canEdit = false;

            Object.keys(this.headerFields).forEach(key => {
                if (key in values) this.$set(this.form, key, values[key]);
            });

            if (values.load_location || values.unload_location) {
                this.$set(this.form, 'date_location', values.date_location ? values.date_location : new Date());
                this.$set(this.form, 'time_location', values.time_location ? values.time_location : new Date());
                this.$set(this.form, 'load_location', values.load_location || '');
                this.$set(this.form, 'unload_location', values.unload_location || '');
            }
            this.$set(this.form, 'lines', values.lines || []);

            if (
                values.client_id !== undefined &&
                values.client_id !== null &&
                values.client_estab !== undefined &&
                values.client_estab !== null
            ) {
                this.$set(
                    this.form,
                    'client_id',
                    `${values.client_id}-${values.client_estab}`
                );
                this.$set(this.form, 'client_estab', values.client_estab);
            }
        },

        async onDossierTypeChange() {
            if (!this.form.tipodoc) return;
            try {
                this.loading = true;
                const response = await dossierService.getFormConfig(this.form.tipodoc);

                const dossierData = response.form;
                this.canEdit = response.can_edit;
                this.formConfig = dossierData.schema;
                this.title = dossierData.title || this.title;
                this.nextDossierNumber = response.next_obra_no || '';

                this.initTransportFields();
                this.initializeFormFields();
                this.form.lines = [];
            } finally { this.loading = false; }
        },

        initTransportFields() {
            Object.entries(this.formConfig.form_fields).forEach(([, config]) => {
                if (config.type === 'transportDocumentField') {
                    this.$set(this.form, 'date_location', new Date());
                    const defaultTime = new Date();
                    defaultTime.setMinutes(defaultTime.getMinutes() + 5);
                    this.$set(this.form, 'time_location', defaultTime);
                    this.$set(this.form, 'load_location', '');
                    this.$set(this.form, 'unload_location', '');
                }
            });
        },

        initializeFormFields() {
            Object.keys(this.headerFields).forEach(key => {
                const config = this.headerFields[key];
                if (config.default === 'today' && config.type === 'date') this.$set(this.form, key, new Date());
                else if (!(key in this.form)) this.$set(this.form, key, null);
            });
        },

        async submitForm() {
            if (!this.validateForm()) return;
            this.loading = true;
            try {

                let payload = {
                    ...this.form,
                    date: this.formatLocalDate(this.form.date),
                };

                if (payload.client_id && typeof payload.client_id === 'string' && payload.client_id.includes('-')) {
                    const [id, estab] = payload.client_id.split('-');
                    payload.client_id = parseInt(id, 10);
                    payload.client_estab = parseInt(estab, 10);
                }

                if (this.form.tipodoc === 11) {
                    payload = {
                        ...payload,
                        date: this.formatLocalDate(this.form.date),
                        date_location: this.formatLocalDate(this.form.date_location),
                        time_location: this.formatLocalTime(this.form.time_location)
                    };
                }

                let result = this.isEditMode
                    ? await dossierService.updateByType(this.dossierStamp, payload)
                    : await dossierService.createByType(payload.tipodoc, payload);

                this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: `Dossier ${result.data.reference} guardado`, life: 3000 });
                this.cancelForm();
            } catch (error) {
                const data = error.response?.data;
                if (data?.error) {
                    let message = data.error;
                    if (data.details) {
                        message += ` (${data.details})`;
                    }
                    if (data.message) {
                        message += ` (${data.message})`;
                    }
                    this.showError(message);
                } else {
                    this.showError('Erro ao guardar dossier');
                }
            } finally { this.loading = false; }
        },

        formatLocalDate(value) {
            if (!value || !(value instanceof Date)) return value;

            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        formatLocalTime(value) {
            if (!value || !(value instanceof Date)) return value;

            const hours = String(value.getHours()).padStart(2, '0');
            const minutes = String(value.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        },

        cancelForm() {
            if (this.isPatMode) {
                this.$emit('closeForm');
            } else {
                this.$closeCurrentTab({ name: 'internalDossiers' });
            }
        },

        formatCurrency(v) { return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0); },
        mergeFormData(data) { this.form = { ...this.form, ...data }; },
        showError(m) { this.$toast.add({ severity: 'error', summary: 'Erro', detail: m, life: 6000 }); },
        async loadDossierTypes() {
            this.loadingTypes = true;
            this.dossierTypes = await dossierService.getConfigTypes();
            this.loadingTypes = false;
        },
        validateForm() {
            this.formErrors = {};
            if (!this.form.tipodoc) { this.formErrors.tipodoc = 'Obrigatório'; return false; }
            Object.keys(this.headerFields).forEach(key => {
                if (this.headerFields[key].required && !this.form[key]) this.formErrors[key] = 'Obrigatório';
            });
            return Object.keys(this.formErrors).length === 0;
        }
    }
};
</script>
